import { assertNever } from "@impero-com/common/utils/obj";

import {
    DashboardFilters,
    TimePeriod,
    CategoryAndTag,
} from "api/generated/model";
import { RenderContentArgs } from "org/pageState/components/pageStateManager";
import { DEFAULT_DASHBOARD_FILTERS } from "org/pageState/defaults";
import { PageStateData } from "org/pageState/models";

import { DashboardFilterType } from "../types";

/**
 * A stricter version of dashboard RenderContentArgs that assumes that
 * pageState and onPageStateChange are always defined.
 */
export interface StrictRenderContentArgs {
    pageState: PageStateData<"Dashboard">;
    onPageStateChange: (pageState: PageStateData<"Dashboard">) => void;
}

/**
 * This function is used to convert the arguments of renderContent function in PageStateManager
 * to a more strict type, which is used in Dashboard page.
 */
export function toStrictRenderContentArgs(
    args: RenderContentArgs<PageStateData<"Dashboard">>
): StrictRenderContentArgs {
    const { pageState, onPageStateChange } = args;
    if (pageState === undefined) {
        throw new Error(
            "Expected pageState to be defined in context of Dashboard"
        );
    }
    if (onPageStateChange === undefined) {
        throw new Error(
            "Expected onPageStateChange to be defined in context of Dashboard"
        );
    }
    return { pageState, onPageStateChange };
}

interface OnChangeCallbacks {
    onTimePeriodChange: (value: TimePeriod) => void;
    onShowInactiveProgramsChange: (checked: boolean) => void;
    onShowArchivedActivitiesChange: (checked: boolean) => void;
    onControlProgramsChange: (controlProgramIds: number[]) => void;
    onTagsChange: (categoryAndTags: CategoryAndTag[]) => void;
    onEntitiesChange: (entities: number[]) => void;
    resetFilterType: (filterType: DashboardFilterType) => void;
}

export function deriveOnChangeCallbacks(
    currentFilters: DashboardFilters,
    onFiltersChange: (newFilters: DashboardFilters) => void
): OnChangeCallbacks {
    const onTimePeriodChange = (newTimePeriod: TimePeriod) => {
        onFiltersChange({
            ...currentFilters,
            timePeriod: newTimePeriod,
        });
    };
    const onShowInactiveProgramsChange = (checked: boolean) => {
        onFiltersChange({
            ...currentFilters,
            showInactivePrograms: checked,
        });
    };
    const onShowArchivedActivitiesChange = (checked: boolean) => {
        onFiltersChange({
            ...currentFilters,
            showArchivedActivities: checked,
        });
    };
    const onControlProgramsChange = (controlProgramIds: number[]) => {
        onFiltersChange({
            ...currentFilters,
            controlPrograms: controlProgramIds,
        });
    };
    const onTagsChange = (categoryAndTags: CategoryAndTag[]) => {
        onFiltersChange({
            ...currentFilters,
            tags: categoryAndTags,
        });
    };
    const onEntitiesChange = (entityIds: number[]) => {
        onFiltersChange({
            ...currentFilters,
            entities: entityIds,
        });
    };

    const resetFilterType = (filterType: DashboardFilterType) => {
        switch (filterType) {
            case "ControlPrograms":
                onControlProgramsChange([]);
                break;
            case "Tags":
                onTagsChange([]);
                break;
            case "Entities":
                onEntitiesChange([]);
                break;
            default:
                assertNever(filterType);
        }
    };

    return {
        onTimePeriodChange,
        onShowInactiveProgramsChange,
        onShowArchivedActivitiesChange,
        onControlProgramsChange,
        onTagsChange,
        onEntitiesChange,
        resetFilterType,
    };
}

export function deriveFilterTypes(
    filters: DashboardFilters
): DashboardFilterType[] {
    const filterTypes: DashboardFilterType[] = [];
    if (filters.controlPrograms.length > 0) {
        filterTypes.push("ControlPrograms");
    }
    if (filters.tags.length > 0) {
        filterTypes.push("Tags");
    }
    if (filters.entities.length > 0) {
        filterTypes.push("Entities");
    }
    return filterTypes;
}

/**
 * Counts how many filters are active (differ from their defaults).
 */
export function countActiveFilters(filters: DashboardFilters): number {
    const activeByFilter = {
        timePeriod:
            filters.timePeriod.type !==
            DEFAULT_DASHBOARD_FILTERS.timePeriod.type,
        showInactivePrograms: filters.showInactivePrograms,
        showArchivedActivities: filters.showArchivedActivities,
        tags: filters.tags.length > 0,
        controlPrograms: filters.controlPrograms.length > 0,
        entities: filters.entities.length > 0,
    } satisfies Record<keyof DashboardFilters, boolean>;

    return Object.values(activeByFilter).filter(Boolean).length;
}
